// modelService.js
// Service for handling the ONNX model loading and storage
// Uses OPFS ONLY for model storage (no IndexedDB fallback)
import config from '../config';
import * as Sentry from '@sentry/react';
import { checkStorageForModel } from '../utils/storageCheck';
import {
  storeModelInOPFS,
  getModelFromOPFS,
  checkModelInOPFS,
  cleanupOldModelsOPFS,
  cleanupAllOldModels,
  getModelStorageInfo,
  checkModelIntegrity,
  deleteModelFromOPFS,
  getFileSizeInOPFS,
  getFileContentFromOPFS,
  listOPFSFiles,
  clearAllOPFSFiles
} from '../utils/modelStorage';
import {
  getPersistedModelDownloadVersion,
  setPersistedModelDownloadVersion,
  clearLegacyIndexedDbModelBlobs,
} from '../db';

// Download order used by splash (same on first load and retry):
// 1. DRDME 2. Fundus 3. Eyes 4. train_features_mean 5. train_features_covinv
// Each step: if full model in OPFS → skip; else download (resume from partial % if .partial exists).
// Model keys for OPFS storage (matching API response keys)
const DRDME_MODEL_KEY = 'drdme_join'; // DRDME classifier model (main model)
const FUNDUS_MODEL_KEY = 'fundus_detector'; // Fundus detector model
const EYES_MODEL_KEY = 'eyes_detector'; // Eyes detector model
const FUNDUS_PARTIAL_KEY = `${FUNDUS_MODEL_KEY}.partial`;
const EYES_PARTIAL_KEY = `${EYES_MODEL_KEY}.partial`;

// Binary file keys for OPFS storage
const TRAIN_FEATURES_MEAN_KEY = 'train_features_mean'; // Mean vector for Mahalanobis distance
const TRAIN_FEATURES_COVINV_KEY = 'train_features_covinv'; // Inverse covariance matrix for Mahalanobis distance

/** `download_urls` keys tried for fundus detector ONNX (file still stored under OPFS key fundus_detector). Includes `input_validation` when backend uses that name. */
const FUNDUS_DOWNLOAD_URL_KEYS = [
  'fundus_detector',
  'fundus_model',
  'fundus',
  'input_validation',
];

function pickFundusPresignedUrl(downloadUrls) {
  if (!downloadUrls || typeof downloadUrls !== 'object') {
    return { url: null, key: null };
  }
  for (const key of FUNDUS_DOWNLOAD_URL_KEYS) {
    const u = downloadUrls[key];
    if (typeof u === 'string' && u.length > 0) {
      return { url: u, key };
    }
  }
  return { url: null, key: null };
}

// Legacy alias for backward compatibility
const MODEL_KEY = DRDME_MODEL_KEY;
// Key for partial download (resumable); cleared on success
const PARTIAL_KEY = `${MODEL_KEY}.partial`;

/** Legacy localStorage key; version is stored in IndexedDB (`app_meta`) so it survives logout. */
export const MODEL_VERSION_STORAGE_KEY = 'optha_model_download_version';

/** Same-tab guard: after a version-mismatch purge, avoid purging again before localStorage is updated. */
const MODEL_VERSION_SESSION_PURGED_KEY = 'optha_model_version_session_purged';

let modelDownloadApiPayloadCache = null;

export function invalidateModelDownloadApiCache() {
  modelDownloadApiPayloadCache = null;
}

/**
 * Fetches `/api/reports/model/download/` once per cache cycle; reused by URL helpers and version sync.
 * @returns {Promise<object>} Raw JSON (may include model_version, download_urls, download_url)
 */
async function fetchModelDownloadPayload() {
  if (modelDownloadApiPayloadCache) {
    return modelDownloadApiPayloadCache;
  }
  const token = localStorage.getItem('optha-token');
  if (!token) {
    throw new Error('No auth token found');
  }
  const url = `${config.API_BASE_URL}/api/reports/model/download/`;
  const response = await fetch(url, {
    headers: {
      Authorization: `Token ${token}`,
      Accept: 'application/json',
    },
  });
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Authentication failed. Please log in again.');
    }
    const errorText = await response.text().catch(() => 'No error details');
    throw new Error(`Failed to get model metadata: ${response.status} ${response.statusText} ${errorText}`);
  }
  const data = await response.json();
  modelDownloadApiPayloadCache = data;
  return data;
}

async function invalidateMahalanobisInMemoryCache() {
  try {
    const mod = await import('../utils/imageProcessing');
    if (typeof mod.clearMahalanobisFilesCache === 'function') {
      mod.clearMahalanobisFilesCache();
    }
  } catch (e) {
    // imageProcessing may not be loaded yet
  }
}

async function clearBundleModelFilesFromOPFS() {
  const keys = [
    MODEL_KEY,
    PARTIAL_KEY,
    FUNDUS_MODEL_KEY,
    FUNDUS_PARTIAL_KEY,
    EYES_MODEL_KEY,
    EYES_PARTIAL_KEY,
    TRAIN_FEATURES_MEAN_KEY,
    TRAIN_FEATURES_COVINV_KEY,
  ];
  for (const key of keys) {
    try {
      await deleteModelFromOPFS(key);
    } catch (e) {
      // ignore
    }
  }
  await invalidateMahalanobisInMemoryCache();
  await clearLegacyIndexedDbModelBlobs();
}

/**
 * If the API returns `model_version` and it differs from the last successful bundle, remove cached OPFS models.
 * @returns {Promise<{ didPurge: boolean, serverVersion: string | null }>}
 */
export async function syncCachedModelsWithServerVersion() {
  try {
    const data = await fetchModelDownloadPayload();
    const raw = data.model_version;
    if (raw === undefined || raw === null) {
      return { didPurge: false, serverVersion: null };
    }
    const serverVersion = String(raw);
    let stored = await getPersistedModelDownloadVersion();
    if (stored == null && typeof localStorage !== 'undefined') {
      const legacy = localStorage.getItem(MODEL_VERSION_STORAGE_KEY);
      if (legacy != null && legacy !== '') {
        stored = legacy;
        await setPersistedModelDownloadVersion(legacy);
        try {
          localStorage.removeItem(MODEL_VERSION_STORAGE_KEY);
        } catch (e) {
          // ignore
        }
      }
    }
    if (stored === serverVersion) {
      return { didPurge: false, serverVersion };
    }
    const sessionPurgedFor = sessionStorage.getItem(MODEL_VERSION_SESSION_PURGED_KEY);
    if (sessionPurgedFor === serverVersion) {
      return { didPurge: false, serverVersion };
    }
    await clearBundleModelFilesFromOPFS();
    sessionStorage.setItem(MODEL_VERSION_SESSION_PURGED_KEY, serverVersion);
    return { didPurge: true, serverVersion };
  } catch (e) {
    return { didPurge: false, serverVersion: null };
  }
}

/** Call after all bundle files are present and loaded (splash success). No-op if server does not send model_version. */
export async function persistModelBundleVersionAfterSuccess(serverVersion) {
  if (serverVersion == null || serverVersion === '') {
    return;
  }
  await setPersistedModelDownloadVersion(String(serverVersion));
  try {
    localStorage.removeItem(MODEL_VERSION_STORAGE_KEY);
  } catch (e) {
    // ignore
  }
  try {
    sessionStorage.removeItem(MODEL_VERSION_SESSION_PURGED_KEY);
  } catch (e) {
    // ignore
  }
}

/**
 * Check if the model exists in OPFS storage
 * @returns {Promise<boolean>} Whether the model exists
 */
export const checkModelExists = async () => {
  try {
    const exists = await checkModelInOPFS(MODEL_KEY);
    return exists;
  } catch (error) {
    return false;
  }
};

// Legacy alias for backward compatibility (deprecated - use checkModelExists)
export const checkModelInIndexedDB = checkModelExists;

/**
 * Get pre-signed URL from backend (legacy function for regular model)
 * Now handles both old format (download_url) and new format (download_urls)
 * @returns {Promise<string>} The pre-signed URL for the regular DRDME model
 */
async function getModelPresignedUrl() {
  try {
    const data = await fetchModelDownloadPayload();
                
    // Handle new format (download_urls) - use drdme_join as the regular model
    if (data.download_urls) {
                  
      // Main DRDME classifier URL — backend may use drdme_join, drdme_joint, or drdme_model
      const presignedUrl =
        data.download_urls.drdme_join ||
        data.download_urls.drdme_joint ||
        data.download_urls.drdme_model;

      if (presignedUrl) {
                return presignedUrl;
      }

      // Last resort: first entry (warn — might not be the DRDME model depending on key order)
      const modelKeys = Object.keys(data.download_urls);
      if (modelKeys.length > 0) {
                return data.download_urls[modelKeys[0]];
      }
      
      throw new Error('No model URLs found in download_urls');
    }
    
    // Handle old format (download_url) for backward compatibility
    if (data.download_url) {
            return data.download_url;
    }
    
    // No URL found
    console.error('[ModelService] No download URL found in response. Response keys:', Object.keys(data));
    throw new Error('No download URL found in response');
  } catch (error) {
    console.error('[ModelService] Error in getModelPresignedUrl:', error);
    throw error;
  }
}

/**
 * Get model download URLs from backend (new API format)
 * @returns {Promise<Object>} Object with download_urls containing model URLs
 */
async function getModelDownloadUrls() {
  try {
    const data = await fetchModelDownloadPayload();
        
    // Handle both old format (download_url) and new format (download_urls)
    if (data.download_urls) {
            return data.download_urls;
    } else if (data.download_url) {
            return { fundus_detector: data.download_url };
    } else {
      console.error('[ModelService] getModelDownloadUrls: No URLs in response. Keys:', Object.keys(data));
      throw new Error('No download URLs found in response');
    }
  } catch (error) {
    console.error('[ModelService] getModelDownloadUrls error:', error?.message || error);
    throw error;
  }
}

/**
 * Many "failed" model downloads are still large (e.g. S3 XML error, HTML, JSON) but are not ONNX protobuf.
 * ONNX Runtime then fails with ERROR_CODE 7 / protobuf parsing failed.
 */
function bufferLooksLikeNonOnnxPayload(buffer) {
  if (!buffer || buffer.byteLength < 8) return false;
  const u = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let i = 0;
  while (i < u.length && i < 64 && (u[i] === 0x20 || u[i] === 0x09 || u[i] === 0x0a || u[i] === 0x0d)) {
    i++;
  }
  if (i >= u.length) return false;
  const b = u[i];
  if (b === 0x3c) {
    const slice = u.slice(i, i + Math.min(512, u.length - i));
    const s = new TextDecoder('utf-8', { fatal: false }).decode(slice);
    if (/<!DOCTYPE/i.test(s) || /<\?xml/i.test(s) || /<html/i.test(s) || /<Error>/i.test(s) || /<Code>/.test(s)) {
      return true;
    }
  }
  if (b === 0x7b) {
    const slice = u.slice(i, i + Math.min(2048, u.length - i));
    const s = new TextDecoder('utf-8', { fatal: false }).decode(slice);
    if (/"error"\s*:/i.test(s) || /"detail"\s*:/i.test(s) || /"message"\s*:/i.test(s)) {
      return true;
    }
  }
  return false;
}

function isValidONNXModel(buffer) {
  if (!buffer || buffer.byteLength <= 100000) return false;
  if (bufferLooksLikeNonOnnxPayload(buffer)) return false;
  return true;
}

async function returnOnnxBufferOrPurgeBadPayload(key, modelBuffer, label) {
  if (!modelBuffer) return null;
  if (bufferLooksLikeNonOnnxPayload(modelBuffer)) {
        try {
      await deleteModelFromOPFS(key);
    } catch (e) {
      // ignore
    }
    return null;
  }
  return modelBuffer;
}

// Global abort controller for model download
let modelDownloadController = null;
let isDownloading = false;

// Global abort controller for fundus model download
let fundusDownloadController = null;
let isFundusDownloading = false;

// Global abort controller for eyes model download
let eyesDownloadController = null;
let isEyesDownloading = false;

/**
 * Load the model from S3 and save it to Dexie
 * @param {Function} onProgress - Optional progress callback (progress) => void
 * @param {Function} onResume - Optional callback when resuming from partial (percent) => void
 * @returns {Promise<ArrayBuffer>} The model data
 */
export const loadAndStoreModel = async (onProgress, onResume) => {
  let presignedUrl = null; // Declare outside try block for error reporting
  
  try {
    // Check if already downloading
    if (isDownloading) {
      // Wait for current download to complete
      while (isDownloading) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      // Check if model was downloaded successfully
      const modelData = await getModelFromOPFS(MODEL_KEY);
      if (modelData) {
        return modelData;
      }
    }
    
    // Cancel any existing download
    if (modelDownloadController) {
      modelDownloadController.abort();
      // Wait a bit for the abort to take effect
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    // Set downloading flag
    isDownloading = true;
    
    // Create new abort controller
    modelDownloadController = new AbortController();
    
    // Detect Safari mobile for longer timeout (slower connections)
    const isSafariMobile = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) && 
                           /iPhone|iPad|iPod/i.test(navigator.userAgent);
    // Longer timeout for Safari mobile and when using tunnels (Cloudflare tunnel can be slow)
    const isTunnel = window.location.hostname.includes('trycloudflare.com') || 
                     window.location.hostname.includes('loca.lt') ||
                     window.location.hostname.includes('ngrok.io');
    const timeoutDuration = isSafariMobile || isTunnel ? 600000 : 300000; // 10 minutes for Safari/tunnel, 5 minutes otherwise
    
    // Set timeout
    const timeoutId = setTimeout(() => {
      if (modelDownloadController) {
        modelDownloadController.abort();
      }
    }, timeoutDuration);
    
    presignedUrl = await getModelPresignedUrl();
    
    // Resumable download: check for existing partial file
    let partialSize = 0;
    try {
      partialSize = await getFileSizeInOPFS(PARTIAL_KEY);
      if (partialSize === 0) {
        try {
          await deleteModelFromOPFS(PARTIAL_KEY);
        } catch (e) {
          // Ignore - may not exist
        }
      }
    } catch (e) {
      // Ignore - treat as no partial
    }

    const fetchHeaders = { 'Accept': 'application/octet-stream' };
    if (partialSize > 0) {
      fetchHeaders['Range'] = `bytes=${partialSize}-`;
    }

    const response = await fetch(presignedUrl, {
      signal: modelDownloadController ? modelDownloadController.signal : undefined,
      headers: fetchHeaders,
    });
    
    clearTimeout(timeoutId); // Clear timeout if download starts successfully
    
    if (!response.ok) {
      throw new Error(`Failed to fetch model: ${response.status} ${response.statusText}`);
    }

    // Total size: from Content-Range (206) or Content-Length (200)
    const contentRange = response.headers.get('Content-Range');
    const contentLengthHeader = response.headers.get('Content-Length');
    let totalSize = 0;
    if (response.status === 206 && contentRange) {
      const match = contentRange.match(/bytes \d+-\d+\/(\d+)/) || contentRange.match(/bytes \*\/(\d+)/);
      if (match) totalSize = parseInt(match[1], 10);
    }
    if (totalSize <= 0 && contentLengthHeader) {
      totalSize = parseInt(contentLengthHeader, 10) || 0;
    }
    // If we requested Range but got 200, server doesn't support Range; body is full file
    const rangeIgnored = partialSize > 0 && response.status === 200;
    if (rangeIgnored && contentLengthHeader) {
      totalSize = parseInt(contentLengthHeader, 10) || 0;
    }
    
    const contentLength = totalSize || +contentLengthHeader;
    if (onResume && partialSize > 0 && contentLength) {
      const initialPercent = Math.min(100, Math.round((partialSize / contentLength) * 100));
      onResume(initialPercent);
    }
    
    // Track download progress if callback provided
    let modelBuffer;
    
    if (onProgress && response.body) {
      let reader;
      try {
        reader = response.body.getReader();
      } catch (readerError) {
        throw new Error(`Failed to create stream reader: ${readerError.message || 'Load failed'}`);
      }
      let receivedLength = 0;
      const chunks = [];
      let usedFallback = false;
      
      try {
        while (true) {
          let readResult;
          try {
            readResult = await reader.read();
          } catch (readError) {
            throw new Error(`Stream read failed: ${readError.message || 'Load failed'}`);
          }
          
          const { done, value } = readResult;
          
          if (done) {
            break;
          }
          
          if (!value || value.length === 0) {
            continue;
          }
          
          chunks.push(value);
          receivedLength += value.length;
          
          if (contentLength || totalSize) {
            const total = totalSize || contentLength;
            const received = partialSize + receivedLength;
            const progress = total ? Math.round((received / total) * 100) : 0;
            onProgress(Math.min(100, progress));
          }
        }
        } catch (streamError) {
        // User cancelled (Retry) – don't save partial or fallback, just rethrow so outer catch returns null
        const isAbort = streamError?.name === 'AbortError' || streamError?.message?.includes('aborted');
        if (isAbort) {
          try {
            reader.cancel();
          } catch (e) {
            // Ignore
          }
          throw streamError;
        }
        // Save partial on stream error so next run can resume
        if (chunks.length > 0 && receivedLength > 0) {
          try {
            const partialBuffer = new Uint8Array(receivedLength);
            let pos = 0;
            for (const c of chunks) {
              partialBuffer.set(c, pos);
              pos += c.length;
            }
            await storeModelInOPFS(PARTIAL_KEY, partialBuffer);
          } catch (saveErr) {
            /* ignore */
          }
        }
        // Cancel the reader
        try {
          reader.cancel();
        } catch (cancelError) {
          // Could not cancel reader
        }
        // If streaming fails, try arrayBuffer fallback (Cloudflare Tunnel can be unstable)
        const totalForPct = totalSize || contentLength;
        const progressPercent = totalForPct ? ((partialSize + receivedLength) / totalForPct) * 100 : 0;
        const isTunnel = window.location.hostname.includes('trycloudflare.com') ||
                         window.location.hostname.includes('loca.lt') ||
                         window.location.hostname.includes('ngrok.io');
        if (isTunnel || progressPercent < 50) {
          const fallbackHeaders = { 'Accept': 'application/octet-stream' };
          if (partialSize > 0) fallbackHeaders['Range'] = `bytes=${partialSize}-`;
          const fallbackResponse = await fetch(presignedUrl, {
            signal: modelDownloadController ? modelDownloadController.signal : undefined,
            headers: fallbackHeaders,
          });
          if (!fallbackResponse.ok) {
            throw new Error(`Fallback fetch failed: ${fallbackResponse.status} ${fallbackResponse.statusText}`);
          }
          const fallbackAb = await fallbackResponse.arrayBuffer();
          if (partialSize > 0 && fallbackResponse.status === 206) {
            const partialBuf = await getFileContentFromOPFS(PARTIAL_KEY);
            if (partialBuf && partialBuf.byteLength === partialSize) {
              modelBuffer = new Uint8Array(partialSize + fallbackAb.byteLength);
              modelBuffer.set(new Uint8Array(partialBuf), 0);
              modelBuffer.set(new Uint8Array(fallbackAb), partialSize);
            } else {
              modelBuffer = new Uint8Array(fallbackAb);
            }
          } else {
            modelBuffer = new Uint8Array(fallbackAb);
          }
          usedFallback = true;
        } else {
          throw streamError;
        }
      }

      if (!usedFallback) {
        // Combine chunks into single buffer; prepend existing partial if resuming (206)
        const newPart = new Uint8Array(receivedLength);
        let position = 0;
        for (const chunk of chunks) {
          newPart.set(chunk, position);
          position += chunk.length;
        }
        if (partialSize > 0 && response.status === 206 && !rangeIgnored) {
          const partialBuf = await getFileContentFromOPFS(PARTIAL_KEY);
          if (partialBuf && partialBuf.byteLength === partialSize) {
            modelBuffer = new Uint8Array(partialSize + receivedLength);
            modelBuffer.set(new Uint8Array(partialBuf), 0);
            modelBuffer.set(newPart, partialSize);
          } else {
            modelBuffer = newPart;
          }
        } else if (rangeIgnored) {
          modelBuffer = newPart;
        } else {
          modelBuffer = newPart;
        }
      }
    } else {
      const ab = await response.arrayBuffer();
      if (partialSize > 0 && response.status === 206) {
        const partialBuf = await getFileContentFromOPFS(PARTIAL_KEY);
        if (partialBuf && partialBuf.byteLength === partialSize) {
          modelBuffer = new Uint8Array(partialSize + ab.byteLength);
          modelBuffer.set(new Uint8Array(partialBuf), 0);
          modelBuffer.set(new Uint8Array(ab), partialSize);
        } else {
          modelBuffer = new Uint8Array(ab);
        }
      } else {
        modelBuffer = new Uint8Array(ab);
      }
    }
    
    if (bufferLooksLikeNonOnnxPayload(modelBuffer)) {
      throw new Error(
        'Downloaded file is not a valid ONNX model (response looks like HTML/JSON/XML — check presigned URL, CORS, and auth).'
      );
    }
    if (!isValidONNXModel(modelBuffer)) {
      throw new Error(`Downloaded model file is too small to be valid. Size: ${modelBuffer.byteLength} bytes`);
    }
    
    // Clean up old model versions FIRST to free up space
    try {
      await cleanupOldModelsOPFS(MODEL_KEY);
    } catch (cleanupError) {
      // Continue anyway - cleanup failure shouldn't block new model storage
    }
    
    // Check storage quota before storing model
    const storageCheck = await checkStorageForModel();
    
    if (!storageCheck.hasSpace) {
      throw new Error(`Storage full: ${storageCheck.message}`);
    }
    
    // Delete existing model file if it exists (to avoid quota issues)
    try {
      const exists = await checkModelInOPFS(MODEL_KEY);
      if (exists) {
        await deleteModelFromOPFS(MODEL_KEY);
      }
    } catch (deleteError) {
      // Continue anyway - might not exist
    }
    
    // Store model using OPFS
    await storeModelInOPFS(MODEL_KEY, modelBuffer);

    // Clear partial file so next download starts fresh
    try {
      await deleteModelFromOPFS(PARTIAL_KEY);
    } catch (e) {
      // Ignore
    }
    
    // Clear the controller after successful completion
    modelDownloadController = null;
    isDownloading = false;
        return modelBuffer;
  } catch (error) {
    // Clear the controller on error
    modelDownloadController = null;
    isDownloading = false;
        if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
      // Don't throw for user cancel (Retry) – return null so caller can retry cleanly
      return null;
    }
    
    // Check if it's a network error
    const isNetworkError = 
      error.message.includes('Failed to fetch') ||
      error.message.includes('NetworkError') ||
      error.message.includes('Network request failed') ||
      (error.name === 'TypeError' && error.message.includes('fetch'));
    
    // Only track network errors if user appears to be online (might indicate server issue)
    // Don't track if user is offline (expected behavior)
    if (isNetworkError && navigator.onLine) {
      Sentry.captureException(error, {
        tags: {
          errorType: 'model_download_network_error',
          modelVersion: MODEL_KEY,
          userOnline: navigator.onLine,
        },
        extra: {
          presignedUrlAvailable: presignedUrl ? 'yes' : 'no',
          errorName: error.name,
          errorMessage: error.message,
        },
        level: 'warning', // Lower severity for network issues
      });
    } else if (!isNetworkError) {
      // Non-network errors - always track (server errors, validation errors, etc.)
      Sentry.captureException(error, {
        tags: {
          errorType: 'model_download_error',
          modelVersion: MODEL_KEY,
        },
        extra: {
          presignedUrlAvailable: presignedUrl ? 'yes' : 'no',
          errorName: error.name,
          errorMessage: error.message,
        },
      });
    }
    // If network error and user is offline, don't track (expected)
    
    // For any error, throw a generic error message
    throw new Error(`Model download failed: ${error.message}`);
  }
};

/**
 * Cancel ongoing model download
 */
export const cancelModelDownload = () => {
  if (modelDownloadController) {
    modelDownloadController.abort();
    modelDownloadController = null;
    isDownloading = false;
  }
};

/**
 * Cancel ongoing fundus model download
 */
export const cancelFundusModelDownload = () => {
  if (fundusDownloadController) {
    fundusDownloadController.abort();
    fundusDownloadController = null;
    isFundusDownloading = false;
  }
};

/**
 * Cancel ongoing eyes model download
 */
export const cancelEyesModelDownload = () => {
  if (eyesDownloadController) {
    eyesDownloadController.abort();
    eyesDownloadController = null;
    isEyesDownloading = false;
  }
};

/**
 * Check if a model download is currently in progress
 */
export const isModelDownloadInProgress = () => {
  return isDownloading;
};

/**
 * Check if fundus model download is currently in progress
 */
export const isFundusModelDownloadInProgress = () => {
  return isFundusDownloading;
};

/**
 * Check if eyes model download is currently in progress
 */
export const isEyesModelDownloadInProgress = () => {
  return isEyesDownloading;
};

/**
 * Get the model from OPFS storage (OPFS only, no IndexedDB fallback)
 * @returns {Promise<ArrayBuffer>} The model data
 */
export const getModelFromStorage = async () => {
  try {
    // Check model integrity first
    const integrity = await checkModelIntegrity(MODEL_KEY);
    
    if (!integrity.exists) {
      return null;
    }
    
    if (!integrity.isValid) {
      try {
        await deleteModelFromOPFS(MODEL_KEY);
      } catch (deleteError) {
        // Failed to delete corrupted model
      }
      return null;
    }
    
    const modelBuffer = await getModelFromOPFS(MODEL_KEY);
    
    if (modelBuffer) {
      // Validate buffer size matches file size
      if (modelBuffer.byteLength !== integrity.size) {
        throw new Error(`Model read incomplete. File: ${integrity.size} bytes, Buffer: ${modelBuffer.byteLength} bytes`);
      }

      const usable = await returnOnnxBufferOrPurgeBadPayload(MODEL_KEY, modelBuffer, 'DRDME');
      return usable;
    } else {
      return null;
    }
  } catch (error) {
    return null;
  }
};

// Legacy alias for backward compatibility (deprecated - use getModelFromStorage)
export const getModelFromIndexedDB = getModelFromStorage;

/**
 * Check if fundus detector model exists in OPFS storage
 * @returns {Promise<boolean>} Whether the model exists
 */
export const checkFundusModelExists = async () => {
  try {
    const exists = await checkModelInOPFS(FUNDUS_MODEL_KEY);
    return exists;
  } catch (error) {
    return false;
  }
};

/**
 * Download and store fundus detector model
 * @param {Function} onProgress - Optional progress callback (progress) => void
 * @param {Function} onResume - Optional callback when resuming from partial (percent) => void
 * @returns {Promise<ArrayBuffer>} The model data
 */
export const loadAndStoreFundusModel = async (onProgress, onResume) => {
  let presignedUrl = null; // Declare outside try block for error reporting
  
  try {
        
    // Check if already downloading
    if (isFundusDownloading) {
            while (isFundusDownloading) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      // Check if model was downloaded successfully
      const modelData = await getModelFromOPFS(FUNDUS_MODEL_KEY);
      if (modelData) {
                return modelData;
      }
    }
    
    // Check if already exists
    const exists = await checkFundusModelExists();
    if (exists) {
            const modelData = await getModelFromOPFS(FUNDUS_MODEL_KEY);
      if (modelData) {
                return modelData;
      }
    }

    // Cancel any existing download
    if (fundusDownloadController) {
            fundusDownloadController.abort();
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    // Set downloading flag
    isFundusDownloading = true;
    
    // Create new abort controller
    fundusDownloadController = new AbortController();
    
    // Detect Safari mobile for longer timeout
    const isSafariMobile = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) && 
                           /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isTunnel = window.location.hostname.includes('trycloudflare.com') || 
                     window.location.hostname.includes('loca.lt') ||
                     window.location.hostname.includes('ngrok.io');
    const timeoutDuration = isSafariMobile || isTunnel ? 600000 : 300000; // 10 minutes for Safari/tunnel, 5 minutes otherwise
    
        
    // Set timeout
    const timeoutId = setTimeout(() => {
      if (fundusDownloadController) {
                fundusDownloadController.abort();
      }
    }, timeoutDuration);

    // Get model URLs from API
        const downloadUrls = await getModelDownloadUrls();
    
    const { url: fundusUrl } = pickFundusPresignedUrl(downloadUrls);

    if (!fundusUrl) {
      const keys = Object.keys(downloadUrls).join(', ') || '(none)';
      console.error('[FundusModel] No fundus URL. Tried keys:', FUNDUS_DOWNLOAD_URL_KEYS, 'API had:', keys);
      throw new Error(
        `Fundus detector URL missing. Expected one of: ${FUNDUS_DOWNLOAD_URL_KEYS.join(', ')} in download_urls. Got: ${keys}.`
      );
    }

        presignedUrl = fundusUrl;
    
    // Resumable: check for existing partial
    let partialSize = 0;
    try {
      partialSize = await getFileSizeInOPFS(FUNDUS_PARTIAL_KEY);
      if (partialSize === 0) {
        try {
          await deleteModelFromOPFS(FUNDUS_PARTIAL_KEY);
        } catch (e) {
          // Ignore
        }
      }
    } catch (e) {
      // Ignore
    }

    const fetchHeaders = { 'Accept': 'application/octet-stream' };
    if (partialSize > 0) fetchHeaders['Range'] = `bytes=${partialSize}-`;

    const response = await fetch(presignedUrl, {
      signal: fundusDownloadController ? fundusDownloadController.signal : undefined,
      headers: fetchHeaders,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      console.error(`[FundusModel] Fetch failed: ${response.status} ${response.statusText}`);
      throw new Error(`Failed to fetch fundus model: ${response.status} ${response.statusText}`);
    }

    const contentRange = response.headers.get('Content-Range');
    const contentLengthHeader = response.headers.get('Content-Length');
    let totalSize = 0;
    if (response.status === 206 && contentRange) {
      const match = contentRange.match(/bytes \d+-\d+\/(\d+)/) || contentRange.match(/bytes \*\/(\d+)/);
      if (match) totalSize = parseInt(match[1], 10);
    }
    if (totalSize <= 0 && contentLengthHeader) totalSize = parseInt(contentLengthHeader, 10) || 0;
    const rangeIgnored = partialSize > 0 && response.status === 200;
    if (rangeIgnored && contentLengthHeader) totalSize = parseInt(contentLengthHeader, 10) || 0;

    const contentLength = totalSize || +contentLengthHeader;
    if (onResume && partialSize > 0 && contentLength) {
      const initialPercent = Math.min(100, Math.round((partialSize / contentLength) * 100));
      onResume(initialPercent);
    }
    
    let modelBuffer;
    if (onProgress && response.body) {
      let reader;
      try {
        reader = response.body.getReader();
      } catch (readerError) {
        console.error('[FundusModel] Failed to create stream reader:', readerError);
        throw new Error(`Failed to create stream reader: ${readerError.message || 'Load failed'}`);
      }
      let receivedLength = 0;
      const chunks = [];

      try {
        while (true) {
          let readResult;
          try {
            readResult = await reader.read();
          } catch (readError) {
            const isAbort = readError?.name === 'AbortError' || readError?.message?.includes('aborted');
            if (isAbort) {
              try { reader.cancel(); } catch (e) { }
              throw readError;
            }
            console.error('[FundusModel] Stream read error:', readError);
            throw new Error(`Stream read failed: ${readError.message || 'Load failed'}`);
          }
          const { done, value } = readResult;
          if (done) break;
          if (!value || value.length === 0) continue;
          chunks.push(value);
          receivedLength += value.length;
          if (contentLength || totalSize) {
            const total = totalSize || contentLength;
            const received = partialSize + receivedLength;
            const progress = total ? Math.round((received / total) * 100) : 0;
            onProgress(Math.min(100, progress));
          }
        }
        const newPart = new Uint8Array(receivedLength);
        let position = 0;
        for (const chunk of chunks) {
          newPart.set(chunk, position);
          position += chunk.length;
        }
        if (partialSize > 0 && response.status === 206 && !rangeIgnored) {
          const partialBuf = await getFileContentFromOPFS(FUNDUS_PARTIAL_KEY);
          if (partialBuf && partialBuf.byteLength === partialSize) {
            modelBuffer = new Uint8Array(partialSize + receivedLength);
            modelBuffer.set(new Uint8Array(partialBuf), 0);
            modelBuffer.set(newPart, partialSize);
          } else {
            modelBuffer = newPart;
          }
        } else {
          modelBuffer = newPart;
        }
      } catch (streamError) {
        const isAbort = streamError?.name === 'AbortError' || streamError?.message?.includes('aborted');
        if (isAbort) {
          try { reader.cancel(); } catch (e) { }
          throw streamError;
        }
        if (chunks.length > 0 && receivedLength > 0) {
          try {
            const partialBuffer = new Uint8Array(receivedLength);
            let pos = 0;
            for (const c of chunks) {
              partialBuffer.set(c, pos);
              pos += c.length;
            }
            await storeModelInOPFS(FUNDUS_PARTIAL_KEY, partialBuffer);
          } catch (saveErr) {
            /* ignore */
          }
        }
        try {
          reader.cancel();
        } catch (e) { }
        const totalForPct = totalSize || contentLength;
        const progressPercent = totalForPct ? ((partialSize + receivedLength) / totalForPct) * 100 : 0;
        if (isTunnel || progressPercent < 50) {
          const fallbackHeaders = { 'Accept': 'application/octet-stream' };
          if (partialSize > 0) fallbackHeaders['Range'] = `bytes=${partialSize}-`;
          const fallbackResponse = await fetch(presignedUrl, {
            signal: fundusDownloadController ? fundusDownloadController.signal : undefined,
            headers: fallbackHeaders,
          });
          if (!fallbackResponse.ok) throw new Error(`Fallback fetch failed: ${fallbackResponse.status}`);
          const fallbackAb = await fallbackResponse.arrayBuffer();
          if (partialSize > 0 && fallbackResponse.status === 206) {
            const partialBuf = await getFileContentFromOPFS(FUNDUS_PARTIAL_KEY);
            if (partialBuf && partialBuf.byteLength === partialSize) {
              modelBuffer = new Uint8Array(partialSize + fallbackAb.byteLength);
              modelBuffer.set(new Uint8Array(partialBuf), 0);
              modelBuffer.set(new Uint8Array(fallbackAb), partialSize);
            } else {
              modelBuffer = new Uint8Array(fallbackAb);
            }
          } else {
            modelBuffer = new Uint8Array(fallbackAb);
          }
        } else {
          throw streamError;
        }
      }
    } else {
      const ab = await response.arrayBuffer();
      if (partialSize > 0 && response.status === 206) {
        const partialBuf = await getFileContentFromOPFS(FUNDUS_PARTIAL_KEY);
        if (partialBuf && partialBuf.byteLength === partialSize) {
          modelBuffer = new Uint8Array(partialSize + ab.byteLength);
          modelBuffer.set(new Uint8Array(partialBuf), 0);
          modelBuffer.set(new Uint8Array(ab), partialSize);
        } else {
          modelBuffer = new Uint8Array(ab);
        }
      } else {
        modelBuffer = new Uint8Array(ab);
      }
    }
    
    if (bufferLooksLikeNonOnnxPayload(modelBuffer)) {
      throw new Error(
        '[Fundus] Downloaded file is not ONNX (HTML/JSON/XML response). Check presigned URL and auth.'
      );
    }
    if (!isValidONNXModel(modelBuffer)) {
      console.error(`[FundusModel] Invalid model size: ${modelBuffer.byteLength} bytes`);
      throw new Error(`Downloaded fundus model file is too small to be valid. Size: ${modelBuffer.byteLength} bytes`);
    }

    
    // Clean up old model versions FIRST to free up space
    try {
      await cleanupOldModelsOPFS(FUNDUS_MODEL_KEY);
    } catch (cleanupError) {
      /* ignore */
    }

    // Check storage quota before storing model
    const storageCheck = await checkStorageForModel();
    if (!storageCheck.hasSpace) {
      console.error('[FundusModel] Storage full:', storageCheck.message);
      throw new Error(`Storage full: ${storageCheck.message}`);
    }

    // Delete existing model file if it exists
    try {
      const exists = await checkModelInOPFS(FUNDUS_MODEL_KEY);
      if (exists) {
        await deleteModelFromOPFS(FUNDUS_MODEL_KEY);
      }
    } catch (deleteError) {
      /* ignore */
    }

    // Store model using OPFS
    await storeModelInOPFS(FUNDUS_MODEL_KEY, modelBuffer);
    try {
      await deleteModelFromOPFS(FUNDUS_PARTIAL_KEY);
    } catch (e) { }
    
    fundusDownloadController = null;
    isFundusDownloading = false;
        return modelBuffer;
  } catch (error) {
    fundusDownloadController = null;
    isFundusDownloading = false;
        console.error('[FundusModel] Download error:', error);
    
    if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
            return null;
    }
    
    // Check if it's a network error
    const isNetworkError = 
      error.message.includes('Failed to fetch') ||
      error.message.includes('NetworkError') ||
      error.message.includes('Network request failed') ||
      (error.name === 'TypeError' && error.message.includes('fetch'));
    
        
    // Only track network errors if user appears to be online
    if (isNetworkError && navigator.onLine) {
      Sentry.captureException(error, {
        tags: {
          errorType: 'fundus_model_download_network_error',
          modelVersion: FUNDUS_MODEL_KEY,
          userOnline: navigator.onLine,
        },
        extra: {
          presignedUrlAvailable: presignedUrl ? 'yes' : 'no',
          errorName: error.name,
          errorMessage: error.message,
        },
        level: 'warning',
      });
    } else if (!isNetworkError) {
      // Non-network errors - always track
      Sentry.captureException(error, {
        tags: {
          errorType: 'fundus_model_download_error',
          modelVersion: FUNDUS_MODEL_KEY,
        },
        extra: {
          presignedUrlAvailable: presignedUrl ? 'yes' : 'no',
          errorName: error.name,
          errorMessage: error.message,
        },
      });
    }
    
    throw new Error(`Fundus model download failed: ${error.message}`);
  }
};

/**
 * Get fundus detector model from OPFS storage
 * @returns {Promise<ArrayBuffer>} The model data
 */
export const getFundusModelFromStorage = async () => {
  try {
    const integrity = await checkModelIntegrity(FUNDUS_MODEL_KEY);
    
    if (!integrity.exists) {
      return null;
    }
    
    if (!integrity.isValid) {
      try {
        await deleteModelFromOPFS(FUNDUS_MODEL_KEY);
      } catch (deleteError) {
        // Failed to delete corrupted model
      }
      return null;
    }
    
    const modelBuffer = await getModelFromOPFS(FUNDUS_MODEL_KEY);
    
    if (modelBuffer) {
      if (modelBuffer.byteLength !== integrity.size) {
        throw new Error(`Fundus model read incomplete. File: ${integrity.size} bytes, Buffer: ${modelBuffer.byteLength} bytes`);
      }
      const usable = await returnOnnxBufferOrPurgeBadPayload(FUNDUS_MODEL_KEY, modelBuffer, 'Fundus');
      return usable;
    } else {
      return null;
    }
  } catch (error) {
    return null;
  }
};

/**
 * Check if eyes detector model exists in OPFS storage
 * @returns {Promise<boolean>} Whether the model exists
 */
export const checkEyesModelExists = async () => {
  try {
    const exists = await checkModelInOPFS(EYES_MODEL_KEY);
    return exists;
  } catch (error) {
    return false;
  }
};

/**
 * Download and store eyes detector model
 * @param {Function} onProgress - Optional progress callback (progress) => void
 * @param {Function} onResume - Optional callback when resuming from partial (percent) => void
 * @returns {Promise<ArrayBuffer>} The model data
 */
export const loadAndStoreEyesModel = async (onProgress, onResume) => {
  let presignedUrl = null;
  
  try {
        
    // Check if already downloading
    if (isEyesDownloading) {
            while (isEyesDownloading) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      const modelData = await getModelFromOPFS(EYES_MODEL_KEY);
      if (modelData) {
                return modelData;
      }
    }
    
    // Check if already exists
    const exists = await checkEyesModelExists();
    if (exists) {
            const modelData = await getModelFromOPFS(EYES_MODEL_KEY);
      if (modelData) {
                return modelData;
      }
    }

    // Cancel any existing download
    if (eyesDownloadController) {
            eyesDownloadController.abort();
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    // Set downloading flag
    isEyesDownloading = true;
    
    // Create new abort controller
    eyesDownloadController = new AbortController();
    
    // Detect Safari mobile for longer timeout
    const isSafariMobile = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) && 
                           /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isTunnel = window.location.hostname.includes('trycloudflare.com') || 
                     window.location.hostname.includes('loca.lt') ||
                     window.location.hostname.includes('ngrok.io');
    const timeoutDuration = isSafariMobile || isTunnel ? 600000 : 300000;
    
        
    // Set timeout
    const timeoutId = setTimeout(() => {
      if (eyesDownloadController) {
                eyesDownloadController.abort();
      }
    }, timeoutDuration);

    // Get model URLs from API
        const downloadUrls = await getModelDownloadUrls();
        
    const eyesUrl = downloadUrls.eyes_detector;
    
    if (!eyesUrl) {
      console.error('[EyesModel] Eyes detector URL not found in API response. Available keys:', Object.keys(downloadUrls));
      throw new Error('Eyes detector URL not found in API response');
    }

    presignedUrl = eyesUrl;
    
    // Resumable: check for existing partial
    let partialSize = 0;
    try {
      partialSize = await getFileSizeInOPFS(EYES_PARTIAL_KEY);
      if (partialSize === 0) {
        try {
          await deleteModelFromOPFS(EYES_PARTIAL_KEY);
        } catch (e) { }
      }
    } catch (e) { }

    const fetchHeaders = { 'Accept': 'application/octet-stream' };
    if (partialSize > 0) fetchHeaders['Range'] = `bytes=${partialSize}-`;

    const response = await fetch(presignedUrl, {
      signal: eyesDownloadController ? eyesDownloadController.signal : undefined,
      headers: fetchHeaders,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      console.error(`[EyesModel] Fetch failed: ${response.status} ${response.statusText}`);
      throw new Error(`Failed to fetch eyes model: ${response.status} ${response.statusText}`);
    }

    const contentRange = response.headers.get('Content-Range');
    const contentLengthHeader = response.headers.get('Content-Length');
    let totalSize = 0;
    if (response.status === 206 && contentRange) {
      const match = contentRange.match(/bytes \d+-\d+\/(\d+)/) || contentRange.match(/bytes \*\/(\d+)/);
      if (match) totalSize = parseInt(match[1], 10);
    }
    if (totalSize <= 0 && contentLengthHeader) totalSize = parseInt(contentLengthHeader, 10) || 0;
    const rangeIgnored = partialSize > 0 && response.status === 200;
    if (rangeIgnored && contentLengthHeader) totalSize = parseInt(contentLengthHeader, 10) || 0;

    const contentLength = totalSize || +contentLengthHeader;
    if (onResume && partialSize > 0 && contentLength) {
      const initialPercent = Math.min(100, Math.round((partialSize / contentLength) * 100));
      onResume(initialPercent);
    }
    
    let modelBuffer;
    if (onProgress && response.body) {
      let reader;
      try {
        reader = response.body.getReader();
      } catch (readerError) {
        console.error('[EyesModel] Failed to create stream reader:', readerError);
        throw new Error(`Failed to create stream reader: ${readerError.message || 'Load failed'}`);
      }
      let receivedLength = 0;
      const chunks = [];

      try {
        while (true) {
          let readResult;
          try {
            readResult = await reader.read();
          } catch (readError) {
            const isAbort = readError?.name === 'AbortError' || readError?.message?.includes('aborted');
            if (isAbort) {
              try { reader.cancel(); } catch (e) { }
              throw readError;
            }
            console.error('[EyesModel] Stream read error:', readError);
            throw new Error(`Stream read failed: ${readError.message || 'Load failed'}`);
          }
          const { done, value } = readResult;
          if (done) break;
          if (!value || value.length === 0) continue;
          chunks.push(value);
          receivedLength += value.length;
          if (contentLength || totalSize) {
            const total = totalSize || contentLength;
            const received = partialSize + receivedLength;
            const progress = total ? Math.round((received / total) * 100) : 0;
            onProgress(Math.min(100, progress));
          }
        }
        const newPart = new Uint8Array(receivedLength);
        let position = 0;
        for (const chunk of chunks) {
          newPart.set(chunk, position);
          position += chunk.length;
        }
        if (partialSize > 0 && response.status === 206 && !rangeIgnored) {
          const partialBuf = await getFileContentFromOPFS(EYES_PARTIAL_KEY);
          if (partialBuf && partialBuf.byteLength === partialSize) {
            modelBuffer = new Uint8Array(partialSize + receivedLength);
            modelBuffer.set(new Uint8Array(partialBuf), 0);
            modelBuffer.set(newPart, partialSize);
          } else {
            modelBuffer = newPart;
          }
        } else {
          modelBuffer = newPart;
        }
      } catch (streamError) {
        const isAbort = streamError?.name === 'AbortError' || streamError?.message?.includes('aborted');
        if (isAbort) {
          try { reader.cancel(); } catch (e) { }
          throw streamError;
        }
        if (chunks.length > 0 && receivedLength > 0) {
          try {
            const partialBuffer = new Uint8Array(receivedLength);
            let pos = 0;
            for (const c of chunks) {
              partialBuffer.set(c, pos);
              pos += c.length;
            }
            await storeModelInOPFS(EYES_PARTIAL_KEY, partialBuffer);
          } catch (saveErr) {
            /* ignore */
          }
        }
        try {
          reader.cancel();
        } catch (e) { }
        const totalForPct = totalSize || contentLength;
        const progressPercent = totalForPct ? ((partialSize + receivedLength) / totalForPct) * 100 : 0;
        if (isTunnel || progressPercent < 50) {
          const fallbackHeaders = { 'Accept': 'application/octet-stream' };
          if (partialSize > 0) fallbackHeaders['Range'] = `bytes=${partialSize}-`;
          const fallbackResponse = await fetch(presignedUrl, {
            signal: eyesDownloadController ? eyesDownloadController.signal : undefined,
            headers: fallbackHeaders,
          });
          if (!fallbackResponse.ok) throw new Error(`Fallback fetch failed: ${fallbackResponse.status}`);
          const fallbackAb = await fallbackResponse.arrayBuffer();
          if (partialSize > 0 && fallbackResponse.status === 206) {
            const partialBuf = await getFileContentFromOPFS(EYES_PARTIAL_KEY);
            if (partialBuf && partialBuf.byteLength === partialSize) {
              modelBuffer = new Uint8Array(partialSize + fallbackAb.byteLength);
              modelBuffer.set(new Uint8Array(partialBuf), 0);
              modelBuffer.set(new Uint8Array(fallbackAb), partialSize);
            } else {
              modelBuffer = new Uint8Array(fallbackAb);
            }
          } else {
            modelBuffer = new Uint8Array(fallbackAb);
          }
        } else {
          throw streamError;
        }
      }
    } else {
      const ab = await response.arrayBuffer();
      if (partialSize > 0 && response.status === 206) {
        const partialBuf = await getFileContentFromOPFS(EYES_PARTIAL_KEY);
        if (partialBuf && partialBuf.byteLength === partialSize) {
          modelBuffer = new Uint8Array(partialSize + ab.byteLength);
          modelBuffer.set(new Uint8Array(partialBuf), 0);
          modelBuffer.set(new Uint8Array(ab), partialSize);
        } else {
          modelBuffer = new Uint8Array(ab);
        }
      } else {
        modelBuffer = new Uint8Array(ab);
      }
    }
    
    if (bufferLooksLikeNonOnnxPayload(modelBuffer)) {
      throw new Error(
        '[Eyes] Downloaded file is not ONNX (HTML/JSON/XML response). Check presigned URL and auth.'
      );
    }
    if (!isValidONNXModel(modelBuffer)) {
      console.error(`[EyesModel] Invalid model size: ${modelBuffer.byteLength} bytes`);
      throw new Error(`Downloaded eyes model file is too small to be valid. Size: ${modelBuffer.byteLength} bytes`);
    }

    
    // Clean up old model versions
    try {
      await cleanupOldModelsOPFS(EYES_MODEL_KEY);
    } catch (cleanupError) {
      /* ignore */
    }

    // Check storage quota
    const storageCheck = await checkStorageForModel();
    if (!storageCheck.hasSpace) {
      console.error('[EyesModel] Storage full:', storageCheck.message);
      throw new Error(`Storage full: ${storageCheck.message}`);
    }

    // Delete existing model file if it exists
    try {
      const exists = await checkModelInOPFS(EYES_MODEL_KEY);
      if (exists) {
        await deleteModelFromOPFS(EYES_MODEL_KEY);
      }
    } catch (deleteError) {
      /* ignore */
    }

    // Store model using OPFS
    await storeModelInOPFS(EYES_MODEL_KEY, modelBuffer);
    try {
      await deleteModelFromOPFS(EYES_PARTIAL_KEY);
    } catch (e) { }
    
    eyesDownloadController = null;
    isEyesDownloading = false;
    return modelBuffer;
  } catch (error) {
    eyesDownloadController = null;
    isEyesDownloading = false;
        console.error('[EyesModel] Download error:', error);
    
    if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
            return null;
    }
    
    // Check if it's a network error
    const isNetworkError = 
      error.message.includes('Failed to fetch') ||
      error.message.includes('NetworkError') ||
      error.message.includes('Network request failed') ||
      (error.name === 'TypeError' && error.message.includes('fetch'));
    
        
    // Track errors
    if (isNetworkError && navigator.onLine) {
      Sentry.captureException(error, {
        tags: {
          errorType: 'eyes_model_download_network_error',
          modelVersion: EYES_MODEL_KEY,
          userOnline: navigator.onLine,
        },
        extra: {
          presignedUrlAvailable: presignedUrl ? 'yes' : 'no',
          errorName: error.name,
          errorMessage: error.message,
        },
        level: 'warning',
      });
    } else if (!isNetworkError) {
      Sentry.captureException(error, {
        tags: {
          errorType: 'eyes_model_download_error',
          modelVersion: EYES_MODEL_KEY,
        },
        extra: {
          presignedUrlAvailable: presignedUrl ? 'yes' : 'no',
          errorName: error.name,
          errorMessage: error.message,
        },
      });
    }
    
    throw new Error(`Eyes model download failed: ${error.message}`);
  }
};

/**
 * Get eyes detector model from OPFS storage
 * @returns {Promise<ArrayBuffer>} The model data
 */
export const getEyesModelFromStorage = async () => {
  try {
    const integrity = await checkModelIntegrity(EYES_MODEL_KEY);
    
    if (!integrity.exists) {
      return null;
    }
    
    if (!integrity.isValid) {
      try {
        await deleteModelFromOPFS(EYES_MODEL_KEY);
      } catch (deleteError) {
        // Failed to delete corrupted model
      }
      return null;
    }
    
    const modelBuffer = await getModelFromOPFS(EYES_MODEL_KEY);
    
    if (modelBuffer) {
      if (modelBuffer.byteLength !== integrity.size) {
        throw new Error(`Eyes model read incomplete. File: ${integrity.size} bytes, Buffer: ${modelBuffer.byteLength} bytes`);
      }
      const usable = await returnOnnxBufferOrPurgeBadPayload(EYES_MODEL_KEY, modelBuffer, 'Eyes');
      return usable;
    } else {
      return null;
    }
  } catch (error) {
    return null;
  }
};

/**
 * Check if train_features_mean binary file exists in OPFS storage
 * @returns {Promise<boolean>} Whether the file exists
 */
export const checkTrainFeaturesMeanExists = async () => {
  try {
    const exists = await checkModelInOPFS(TRAIN_FEATURES_MEAN_KEY);
    return exists;
  } catch (error) {
    return false;
  }
};

/**
 * Check if train_features_covinv binary file exists in OPFS storage
 * @returns {Promise<boolean>} Whether the file exists
 */
export const checkTrainFeaturesCovinvExists = async () => {
  try {
    const exists = await checkModelInOPFS(TRAIN_FEATURES_COVINV_KEY);
    return exists;
  } catch (error) {
    return false;
  }
};

/**
 * If every bundle file is in OPFS and the API sends model_version, persist it (e.g. ModelContext path without splash).
 */
export async function tryPersistModelBundleVersionWhenComplete() {
  try {
    const data = await fetchModelDownloadPayload();
    if (data.model_version === undefined || data.model_version === null) {
      return;
    }
    const serverVersion = String(data.model_version);
    const [drdme, fundus, eyes, mean, cov] = await Promise.all([
      checkModelExists(),
      checkFundusModelExists(),
      checkEyesModelExists(),
      checkTrainFeaturesMeanExists(),
      checkTrainFeaturesCovinvExists(),
    ]);
    if (drdme && fundus && eyes && mean && cov) {
      await persistModelBundleVersionAfterSuccess(serverVersion);
    }
  } catch (e) {
    // ignore
  }
}

/**
 * Download and store train_features_mean binary file
 * @param {Function} onProgress - Optional progress callback (progress) => void
 * @returns {Promise<ArrayBuffer>} The binary file data
 */
export const loadAndStoreTrainFeaturesMean = async (onProgress) => {
  let presignedUrl = null;
  
  try {
        
    // Check if already exists
    const exists = await checkTrainFeaturesMeanExists();
    if (exists) {
            const fileData = await getModelFromOPFS(TRAIN_FEATURES_MEAN_KEY);
      if (fileData) {
                return fileData;
      }
    }

    // Get download URLs from API
        const downloadUrls = await getModelDownloadUrls();
        
    const meanUrl = downloadUrls.train_features_mean;
    
    if (!meanUrl) {
      console.error('[TrainFeaturesMean] train_features_mean URL not found in API response. Available keys:', Object.keys(downloadUrls));
      throw new Error('train_features_mean URL not found in API response');
    }

    presignedUrl = meanUrl;
    
    // Download file
    const response = await fetch(presignedUrl, {
      headers: {
        'Accept': 'application/octet-stream',
      }
    });

    if (!response.ok) {
      console.error(`[TrainFeaturesMean] Fetch failed: ${response.status} ${response.statusText}`);
      throw new Error(`Failed to fetch train_features_mean: ${response.status} ${response.statusText}`);
    }

    
    // Track download progress if callback provided
    let fileBuffer;
    const contentLength = +response.headers.get('Content-Length');

    if (onProgress && response.body) {
      const reader = response.body.getReader();
      let receivedLength = 0;
      const chunks = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (!value || value.length === 0) continue;

        chunks.push(value);
        receivedLength += value.length;

        if (contentLength) {
          const progress = Math.round((receivedLength / contentLength) * 100);
          onProgress(progress);
        }
      }
      
      // Combine chunks
      fileBuffer = new Uint8Array(receivedLength);
      let position = 0;
      for (const chunk of chunks) {
        fileBuffer.set(chunk, position);
        position += chunk.length;
      }
    } else {
      fileBuffer = await response.arrayBuffer();
    }
    
    
    // Delete existing file if it exists
    try {
      const exists = await checkModelInOPFS(TRAIN_FEATURES_MEAN_KEY);
      if (exists) {
        await deleteModelFromOPFS(TRAIN_FEATURES_MEAN_KEY);
      }
    } catch (deleteError) {
      /* ignore */
    }

    // Store file using OPFS
    await storeModelInOPFS(TRAIN_FEATURES_MEAN_KEY, fileBuffer);
        await invalidateMahalanobisInMemoryCache();

    return fileBuffer;
  } catch (error) {
    console.error('[TrainFeaturesMean] Download error:', error);
    
    Sentry.captureException(error, {
      tags: {
        errorType: 'train_features_mean_download_error',
        fileKey: TRAIN_FEATURES_MEAN_KEY,
      },
      extra: {
        presignedUrlAvailable: presignedUrl ? 'yes' : 'no',
        errorName: error.name,
        errorMessage: error.message,
      },
    });
    
    throw new Error(`train_features_mean download failed: ${error.message}`);
  }
};

/**
 * Download and store train_features_covinv binary file
 * @param {Function} onProgress - Optional progress callback (progress) => void
 * @returns {Promise<ArrayBuffer>} The binary file data
 */
export const loadAndStoreTrainFeaturesCovinv = async (onProgress) => {
  let presignedUrl = null;
  
  try {
        
    // Check if already exists
    const exists = await checkTrainFeaturesCovinvExists();
    if (exists) {
            const fileData = await getModelFromOPFS(TRAIN_FEATURES_COVINV_KEY);
      if (fileData) {
                return fileData;
      }
    }

    // Get download URLs from API
        const downloadUrls = await getModelDownloadUrls();
        
    const covinvUrl = downloadUrls.train_features_covinv;
    
    if (!covinvUrl) {
      console.error('[TrainFeaturesCovinv] train_features_covinv URL not found in API response. Available keys:', Object.keys(downloadUrls));
      throw new Error('train_features_covinv URL not found in API response');
    }

    presignedUrl = covinvUrl;
    
    // Download file
    const response = await fetch(presignedUrl, {
      headers: {
        'Accept': 'application/octet-stream',
      }
    });

    if (!response.ok) {
      console.error(`[TrainFeaturesCovinv] Fetch failed: ${response.status} ${response.statusText}`);
      throw new Error(`Failed to fetch train_features_covinv: ${response.status} ${response.statusText}`);
    }

    
    // Track download progress if callback provided
    let fileBuffer;
    const contentLength = +response.headers.get('Content-Length');

    if (onProgress && response.body) {
      const reader = response.body.getReader();
      let receivedLength = 0;
      const chunks = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (!value || value.length === 0) continue;

        chunks.push(value);
        receivedLength += value.length;

        if (contentLength) {
          const progress = Math.round((receivedLength / contentLength) * 100);
          onProgress(progress);
        }
      }
      
      // Combine chunks
      fileBuffer = new Uint8Array(receivedLength);
      let position = 0;
      for (const chunk of chunks) {
        fileBuffer.set(chunk, position);
        position += chunk.length;
      }
    } else {
      fileBuffer = await response.arrayBuffer();
    }
    
    
    // Delete existing file if it exists
    try {
      const exists = await checkModelInOPFS(TRAIN_FEATURES_COVINV_KEY);
      if (exists) {
        await deleteModelFromOPFS(TRAIN_FEATURES_COVINV_KEY);
      }
    } catch (deleteError) {
      /* ignore */
    }

    // Store file using OPFS
    await storeModelInOPFS(TRAIN_FEATURES_COVINV_KEY, fileBuffer);
    await invalidateMahalanobisInMemoryCache();

    return fileBuffer;
  } catch (error) {
    console.error('[TrainFeaturesCovinv] Download error:', error);
    
    Sentry.captureException(error, {
      tags: {
        errorType: 'train_features_covinv_download_error',
        fileKey: TRAIN_FEATURES_COVINV_KEY,
      },
      extra: {
        presignedUrlAvailable: presignedUrl ? 'yes' : 'no',
        errorName: error.name,
        errorMessage: error.message,
      },
    });
    
    throw new Error(`train_features_covinv download failed: ${error.message}`);
  }
};

/**
 * Get train_features_mean binary file from OPFS storage
 * @returns {Promise<ArrayBuffer>} The binary file data
 */
export const getTrainFeaturesMeanFromStorage = async () => {
  try {
    const fileBuffer = await getModelFromOPFS(TRAIN_FEATURES_MEAN_KEY);
    return fileBuffer;
  } catch (error) {
    return null;
  }
};

/**
 * Get train_features_covinv binary file from OPFS storage
 * @returns {Promise<ArrayBuffer>} The binary file data
 */
export const getTrainFeaturesCovinvFromStorage = async () => {
  try {
    const fileBuffer = await getModelFromOPFS(TRAIN_FEATURES_COVINV_KEY);
    return fileBuffer;
  } catch (error) {
    return null;
  }
};

/**
 * Check model file location and integrity (for debugging)
 * Call this from browser console: window.checkModelLocation()
 */
export const checkModelLocation = async () => {
  const integrity = await checkModelIntegrity(MODEL_KEY);
  const storageInfo = await getModelStorageInfo();
  
  return {
    model: integrity,
    storage: storageInfo
  };
};

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  window.checkModelLocation = checkModelLocation;
  window.deleteModel = async () => {
    await deleteModelFromOPFS(MODEL_KEY);
  };
  window.listOPFSFiles = async () => {
    const files = await listOPFSFiles();
    return files;
  };
  window.clearAllOPFS = async () => {
    const result = await clearAllOPFSFiles();
    return result;
  };
  window.cleanupAllOldModels = async () => {
    const result = await cleanupAllOldModels();
    return result;
  };
}

/**
 * Clean up old model files that don't match the current 3-model system
 * This runs only ONCE per user (uses localStorage flag)
 * Deletes only the old model file(s) that are NOT one of: drdme_join, fundus_detector, eyes_detector
 * @returns {Promise<{deleted: string[], errors: Array}>}
 */
export const cleanupOldModelFiles = async () => {
  try {
    return await cleanupAllOldModels();
  } catch (error) {
    return { deleted: [], errors: [{ name: 'cleanup', error: error.message }] };
  }
};

// Cleanup function is now handled by cleanupOldModelsOPFS in modelStorage.js
// This function is kept for backward compatibility but delegates to OPFS cleanup
async function cleanupOldModels() {
  try {
    await cleanupOldModelsOPFS(MODEL_KEY);
  } catch (error) {
    // Error cleaning up old models
  }
}


export async function initializeModel() {
  try {
    if (!window.ort) {
      throw new Error('ONNX Runtime not loaded yet');
    }
    await syncCachedModelsWithServerVersion();
    let modelData;
    const exists = await checkModelExists();
    if (exists) {
      modelData = await getModelFromStorage();
    } else {
      modelData = await loadAndStoreModel();
    }
    if (!modelData) {
      throw new Error('Failed to get model data');
    }
    const options = {
      executionProviders: ['wasm'],
      graphOptimizationLevel: 'all'
    };
    const session = await window.ort.InferenceSession.create(
      new Uint8Array(modelData),
      options
    );
    return session;
  } catch (error) {
    throw error;
  }
} 